"use client";

import { useTranslation } from "next-i18next";
import { IProductInfo } from "@/props/IProduct";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/router";
import debounce from "lodash/debounce";
import { useAppDispatch } from "@/store/hooks";
import { setCartTotal } from "@/store/cart";
import { cartCountApi, cartSaveApi } from "@/api/cart";
import { createPreOrderApi } from "@/api/cart";
import { userIntegralApi } from "@/api/point";
import Cart from "@/components/Common/Header/Cart";
import { Button, Spin, message } from "antd";
import AddCartSuccessModal from "@/components/Modal/SuccessTipModal";
import TipModal from "@/components/Modal/TipModal";
import { getOptimalApi } from "@/api/voucher";
import Icon from "../Icon";
import ProductFavorite from "./ProductFavorite";
import { Toast } from "@/components/antd-mobile";
import { trackClick } from "@/utils/tracking";

interface IBtnStyle {
  height: string;
}

/**
 * product detail page, action buttons component
 * @param product product info
 * @param specStock multi-specification product stock
 * @param specPrice multi-specification product price
 * @param attrId product specification id
 * @param cartNum product quantity
 * @param isInPopup is use in moible popup
 */
const ProductActionButtons = ({
  product,
  specStock = 0,
  specPrice,
  attrId,
  cartNum,
  isInPopup = false,
}: {
  product?: IProductInfo;
  specStock: number;
  specPrice: number;
  attrId?: number;
  cartNum: number;
  isInPopup?: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const appStore = useAppSelector((state: RootState) => state.app);
  const { isMobile } = appStore;
  const dispatch = useAppDispatch();
  const [cartLoading, setCartLoading] = useState<boolean>(false);
  const [preOrderLoading, setPreOrderLoading] = useState<boolean>(false);
  const [btnStyle, setBtnStyle] = useState<IBtnStyle>();
  const [iconSize, setIconSize] = useState<number>(20);
  const AddCartSuccessRef: any = useRef(null);
  const TipRef: any = useRef(null);
  const userInfo = useAppSelector((state) => state.user.userInfo);
  const token = useAppSelector((state: RootState) => state.user.token);

  /**
   * handle add to cart
   */
  const handleAddToCart = async () => {
    // 先记点击意图，再走登录/校验分支，否则未登录用户的点击会全部丢失
    trackClick("mall_detail_add_to_cart", {
      goods_id: product?.id,
      goods_name: product?.storeName,
      attr_id: attrId,
      cart_num: cartNum,
      price: !product?.specType ? product?.price : specPrice,
      is_login: userInfo ? 1 : 0,
      in_popup: isInPopup ? 1 : 0,
    });
    // user login
    if (!userInfo) {
      return router.push(`/login?redirect=${router.asPath}`);
    }
    if (!product) return;
    if (!attrId) return hanldeShowTips(t("product_detail.please_select_product_info"));
    const data = {
      productId: product.id,
      productAttrUnique: attrId,
      cartNum: cartNum,
    };

    setCartLoading(true);
    try {
      const isCartSave = await cartSaveApi(data);
      setCartLoading(false);
      if (isCartSave === undefined) return;
      AddCartSuccessRef.current.setOpen(true);
      updateCartTotal();
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      setCartLoading(false);
    }
  };

  /**
   * handle create pre-order
   */
  const handleCreatePreOrder = async () => {
    // 同上，点击意图先于任何拦截上报
    trackClick("mall_detail_redeem_now", {
      goods_id: product?.id,
      goods_name: product?.storeName,
      attr_id: attrId,
      cart_num: cartNum,
      price: !product?.specType ? product?.price : specPrice,
      is_login: userInfo ? 1 : 0,
      in_popup: isInPopup ? 1 : 0,
    });
    // user login
    if (!userInfo) {
      return router.push(`/login?redirect=${router.asPath}`);
    }

    if (!product) return;

    if (!attrId) return hanldeShowTips(t("product_detail.please_select_product_info"));

    // product amount
    const price = !product.specType ? product.price : specPrice;
    const amount = price * cartNum;
    setPreOrderLoading(true);
    try {
      // 获取优惠券信息并计算临时价格
      const voucherRes: any = await getOptimalApi({
        preOrderType: "shoppingCart",
        orderDetails: [
          {
            productId: product!.id,
            attrValueId: attrId ?? 0,
            productNum: cartNum,
            price: amount,
          },
        ],
      });
      const discountAmount = voucherRes?.discountAmount || 0;
      const finalPrice = Math.max(amount - discountAmount, 0);
      // refresh user integral
      const userData: any = await userIntegralApi();

      // integral insufficient
      if (finalPrice > userData.integral) {
        setPreOrderLoading(false);
        return TipRef.current.setOpen(true);
      }

      const data = {
        preOrderType: "buyNow",
        orderDetails: [
          {
            productId: product.id,
            attrValueId: attrId,
            productNum: cartNum,
          },
        ],
      };
      const res = await createPreOrderApi(data);
      setPreOrderLoading(false);
      if (!res) return;
      router.push(`/order/confirm?preOrderNo=${res}`);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      setPreOrderLoading(false);
    }
  };

  /**
   * update total shopping cart items
   */
  const updateCartTotal = async () => {
    if (!token) return;
    const data: any = await cartCountApi();
    const count = data["count"] || 0;
    dispatch(setCartTotal(count));
  };

  /**
   * handle show tip
   * @param msg tip text
   */
  const hanldeShowTips = (msg: string) => {
    const ToastIcon = (
      <div className="flex w-full justify-center">
        <Icon name="attention" isMobile size={24} title="attention" className="mb-2" />
      </div>
    );
    if (isMobile) {
      Toast.show({
        content: msg,
        icon: ToastIcon,
      });
    } else {
      message.warning(msg);
    }
  };

  useEffect(() => {
    setBtnStyle(
      isMobile
        ? {
            height: "40px",
          }
        : {
            height: "56px",
          }
    );
    setIconSize(isMobile ? 16 : 20);
  }, [isMobile]);

  // 这样可以避免cart icon的重新渲染导致的闪动，因为如果是使用image，memo也解决不了问题，重新渲染就是会抖动
  const AddtoCartIcon = () => {
    return (
      <svg
        width="20px"
        height="20px"
        viewBox="0 0 20 20"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <title>icon_bag</title>
        <g id="控件" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <g id="header" transform="translate(-1412.000000, -32.000000)">
            <g id="icon_bag" transform="translate(1412.000000, 32.000000)">
              <rect id="矩形" fill="#D8D8D8" opacity="0" x="0" y="0" width="20" height="20"></rect>
              <path
                d="M3.55757335,0.833333333 L3.79539851,2.03333333 L4.38133333,4.99933333 L18.7813159,5 L19.1666667,5.83538674 L17.7694653,11.6666667 L5.71133333,11.6663333 L6.24633333,14.3493333 L16.3623333,14.3493333 L17.4293333,15.4163333 L5.27633333,15.4163333 L5.26572843,15.4197034 L2.54533333,2.03333333 L2.02033333,2.03333333 L0.833333333,0.847333333 L0.833333333,0.833333333 L3.55757335,0.833333333 Z M4.63533333,6.27133333 L5.49033333,10.5563333 L16.9740761,10.5575588 L17.999119,6.27284252 L4.63533333,6.27133333 Z M7.91666667,15.8333333 C8.6070226,15.8333333 9.16666667,16.5795254 9.16666667,17.5 C9.16666667,18.4204746 8.6070226,19.1666667 7.91666667,19.1666667 C7.22631073,19.1666667 6.66666667,18.4204746 6.66666667,17.5 C6.66666667,16.5795254 7.22631073,15.8333333 7.91666667,15.8333333 Z M7.91666667,16.6666667 C7.74714406,16.6666667 7.5,16.9961921 7.5,17.5 C7.5,18.0038079 7.74714406,18.3333333 7.91666667,18.3333333 C8.08618927,18.3333333 8.33333333,18.0038079 8.33333333,17.5 C8.33333333,16.9961921 8.08618927,16.6666667 7.91666667,16.6666667 Z M14.5833333,15.8333333 C15.2736893,15.8333333 15.8333333,16.5795254 15.8333333,17.5 C15.8333333,18.4204746 15.2736893,19.1666667 14.5833333,19.1666667 C13.8929774,19.1666667 13.3333333,18.4204746 13.3333333,17.5 C13.3333333,16.5795254 13.8929774,15.8333333 14.5833333,15.8333333 Z M14.5833333,16.6666667 C14.4138107,16.6666667 14.1666667,16.9961921 14.1666667,17.5 C14.1666667,18.0038079 14.4138107,18.3333333 14.5833333,18.3333333 C14.7528559,18.3333333 15,18.0038079 15,17.5 C15,16.9961921 14.7528559,16.6666667 14.5833333,16.6666667 Z"
                id="形状结合"
                fill="#d91d22"
              ></path>
            </g>
          </g>
        </g>
      </svg>
    );
  };
  const AddtoCartGaryIcon = () => {
    return (
      <svg
        width="20px"
        height="20px"
        viewBox="0 0 20 20"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <title>icon_bag</title>
        <g id="控件" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <g id="header" transform="translate(-1412.000000, -32.000000)">
            <g id="icon_bag" transform="translate(1412.000000, 32.000000)">
              <rect id="矩形" fill="#D8D8D8" opacity="0" x="0" y="0" width="20" height="20"></rect>
              <path
                d="M3.55757335,0.833333333 L3.79539851,2.03333333 L4.38133333,4.99933333 L18.7813159,5 L19.1666667,5.83538674 L17.7694653,11.6666667 L5.71133333,11.6663333 L6.24633333,14.3493333 L16.3623333,14.3493333 L17.4293333,15.4163333 L5.27633333,15.4163333 L5.26572843,15.4197034 L2.54533333,2.03333333 L2.02033333,2.03333333 L0.833333333,0.847333333 L0.833333333,0.833333333 L3.55757335,0.833333333 Z M4.63533333,6.27133333 L5.49033333,10.5563333 L16.9740761,10.5575588 L17.999119,6.27284252 L4.63533333,6.27133333 Z M7.91666667,15.8333333 C8.6070226,15.8333333 9.16666667,16.5795254 9.16666667,17.5 C9.16666667,18.4204746 8.6070226,19.1666667 7.91666667,19.1666667 C7.22631073,19.1666667 6.66666667,18.4204746 6.66666667,17.5 C6.66666667,16.5795254 7.22631073,15.8333333 7.91666667,15.8333333 Z M7.91666667,16.6666667 C7.74714406,16.6666667 7.5,16.9961921 7.5,17.5 C7.5,18.0038079 7.74714406,18.3333333 7.91666667,18.3333333 C8.08618927,18.3333333 8.33333333,18.0038079 8.33333333,17.5 C8.33333333,16.9961921 8.08618927,16.6666667 7.91666667,16.6666667 Z M14.5833333,15.8333333 C15.2736893,15.8333333 15.8333333,16.5795254 15.8333333,17.5 C15.8333333,18.4204746 15.2736893,19.1666667 14.5833333,19.1666667 C13.8929774,19.1666667 13.3333333,18.4204746 13.3333333,17.5 C13.3333333,16.5795254 13.8929774,15.8333333 14.5833333,15.8333333 Z M14.5833333,16.6666667 C14.4138107,16.6666667 14.1666667,16.9961921 14.1666667,17.5 C14.1666667,18.0038079 14.4138107,18.3333333 14.5833333,18.3333333 C14.7528559,18.3333333 15,18.0038079 15,17.5 C15,16.9961921 14.7528559,16.6666667 14.5833333,16.6666667 Z"
                id="形状结合"
                fill="#b5b5b5"
              ></path>
            </g>
          </g>
        </g>
      </svg>
    );
  };
  const BugIcon = () => {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path opacity=".465" d="M0 0h20v20H0z" />
          <path
            d="M16 5.333 20 20H0L4 5.333h12zM14.727 7H5.272L2.18 18.333h15.639L14.727 7zm-1.394 5v1.667L10 15l-3.333-1.333V12l3.341 1.333L13.333 12zm-8-9 4.675-1.333L14.667 3V1.333L10 0 5.333 1.333V3z"
            fill="#FFF"
            fillRule="nonzero"
          />
        </g>
      </svg>
    );
  };
  const BugGaryIcon = () => {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path opacity=".465" d="M0 0h20v20H0z" />
          <path
            d="M16 5.333 20 20H0L4 5.333h12zM14.727 7H5.272L2.18 18.333h15.639L14.727 7zm-1.394 5v1.667L10 15l-3.333-1.333V12l3.341 1.333L13.333 12zm-8-9 4.675-1.333L14.667 3V1.333L10 0 5.333 1.333V3z"
            fill="#b5b5b5"
            fillRule="nonzero"
          />
        </g>
      </svg>
    );
  };

  // add to the cart element
  const AddtoCartEl = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <div className="flex w-full items-center justify-center text-sm lg:text-base">
        <div className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} svg-box relative mr-2 2xs:mr-4`}>
          {disabled ? <AddtoCartGaryIcon /> : <AddtoCartIcon />}
        </div>
        {t("product_detail.add_to_the_cart")}
      </div>
    );
  };

  // create order element
  const CreateOrderEl = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <div className="flex w-full items-center justify-center text-sm lg:text-base">
        <div className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} svg-box relative mr-2 2xs:mr-4`}>
          {disabled ? <BugGaryIcon /> : <BugIcon />}
        </div>
        {t("product_detail.redeem_now")}
      </div>
    );
  };

  // favorite button element - only shown on PC (mobile uses top-right header)
  const FavoriteBtn = () => {
    return (
      <div className="hidden lg:block">
        <ProductFavorite product={product} />
      </div>
    );
  };
  // whether the product is mode 3 (redeem only, no cart)
  const isRedeemOnly = product?.productMode === 3;

  // disabled buttons
  const DisabledButtons = () => {
    return (
      <>
        {!isRedeemOnly && (
          <Button
            size="large"
            className="ant-disabled-button min-w-0 mr-mobile w-[33.33vw] cursor-not-allowed lg:mr-4 lg:w-auto lg:min-w-[212px]"
            style={btnStyle}
          >
            <AddtoCartEl disabled={true} />
          </Button>
        )}
        <Button
          size="large"
          className="ant-disabled-button min-w-0 flex-1 cursor-not-allowed bg-gray-100  lg:min-w-[270px] lg:flex-none xl:min-w-[320px]"
          style={btnStyle}
        >
          <CreateOrderEl disabled={true} />
        </Button>
        <FavoriteBtn />
      </>
    );
  };

  // available buttons
  const AvailableButtons = () => {
    return (
      <>
        {!isRedeemOnly && (
          <Spin spinning={cartLoading}>
            <Button
              type="primary"
              size="large"
              className="min-w-0 mr-mobile w-[33.33vw] lg:mr-4 lg:w-auto lg:min-w-[212px] "
              ghost
              style={btnStyle}
              onClick={debounce(handleAddToCart, 300)}
            >
              <AddtoCartEl disabled={false} />
            </Button>
          </Spin>
        )}
        <div className="flex-1 lg:flex-none">
          <Spin spinning={preOrderLoading}>
            <Button
              type="primary"
              size="large"
              className="min-w-0  w-full lg:min-w-[270px] lg:flex-none xl:min-w-[320px]"
              style={btnStyle}
              onClick={debounce(handleCreatePreOrder, 300)}
            >
              <CreateOrderEl disabled={false} />
            </Button>
          </Spin>
        </div>
        <FavoriteBtn />
      </>
    );
  };

  return (
    <>
      <div
        className={`z-full fixed bottom-0 left-0 z-30 mt-0 flex w-full items-center border-b-0 border-t border-gray-100 bg-white px-mobile pb-2 pt-2 lg:static lg:w-auto lg:border-b lg:border-t-0 lg:bg-none lg:px-0 lg:pb-8 lg:pt-0 ${
          !product?.specType ? "lg:mt-24" : "lg:mt-6"
        }`}
      >
        {!isInPopup && (
          <div className="block lg:hidden">
            <Cart />
          </div>
        )}

        {(product?.specType && specStock === 0) || (!product?.specType && product?.stock === 0) ? (
          <DisabledButtons />
        ) : (
          <AvailableButtons />
        )}
      </div>

      <AddCartSuccessModal
        onRef={AddCartSuccessRef}
        imgSrc="/images/cart/cart.webp"
        desc={t("product_detail.add_to_the_cart_success")}
        autoClose={true}
      />

      <TipModal
        onRef={TipRef}
        title={t("cart.balance_not_enough")}
        desc={t("cart.balance_not_enough_to_exchange")}
        qtScene="mall_detail_balance_not_enough"
      />

      <style global jsx>
        {`
          .ant-disabled-button {
            background: #f1f1f1;
            border: 0;
            color: #b5b5b5 !important;
          }
          .svg-box svg {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
          }
        `}
      </style>
    </>
  );
};

export default ProductActionButtons;
